import React, { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Modal,
  Select,
  Spinner,
  Table,
  TextInput,
  Textarea,
} from "flowbite-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import moment from "moment";
import axios from "../config/axiosInstance";
import { ADMIN, CRM, TEAMS, USERS } from "../config/apiConfig";
import CorporateBreadcrumb from "../components/common/CorporateBreadcrumb";

const PERMISSION_LABELS = {
  POWER_USER: "Power User",
  ADMIN: "Administrador",
  CLIENTE: "Cliente",
  REPRESENTANTE: "Representante",
};

const PERMISSION_OPTIONS = Object.entries(PERMISSION_LABELS).map(
  ([value, label]) => ({ value, label })
);

const formatCurrency = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = moment(value);
  if (!date.isValid()) return "—";
  return date.format("DD/MM/YYYY");
};

const badgeColorFromStatus = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized.includes("trial") || normalized.includes("teste"))
    return "warning";
  if (normalized.includes("cancel") || normalized.includes("expir"))
    return "failure";
  return "success";
};

const CompanyDetailPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery(
    ["company-detail", companyId],
    async () => {
      const response = await axios.get(ADMIN.GET_COMPANY_DETAIL(companyId));
      return response.data?.data || null;
    },
    { enabled: Boolean(companyId), staleTime: 5 * 60 * 1000 }
  );

  const company = data?.company;
  const subscription = data?.subscription;
  const charges = data?.charges || [];
  const teams = data?.teams || [];
  const funnels = data?.funnels || [];
  const users = data?.users || [];
  const planName =
    subscription?.planName ||
    company?.plan?.name ||
    company?.plan?.code ||
    "Plano não informado";
  const companyName = company?.socialReason || company?.name || "Empresa";
  const planCycleLabel =
    subscription?.planCycle?.description ||
    (subscription?.planCycle?.intervalMonths
      ? `${subscription.planCycle.intervalMonths} meses`
      : "-");
  const planExpiration =
    subscription?.planExpirationDate || company?.planExpirationDate;
  const planStatus = subscription?.planStatus || "Ativo";
  const isTrial = !company?.planId || !company?.planCycleId;
  const badgeColor =
    badgeColorFromStatus(planStatus) || (isTrial ? "warning" : "success");

  const summaryStats = [
    { label: "Funis", value: funnels.length },
    { label: "Times", value: teams.length },
    { label: "Usuários", value: users.length },
    { label: "Pagamentos", value: charges.length },
  ];

  const addressParts = [
    company?.address,
    company?.logradouro,
    company?.street,
    company?.tipo_logradouro,
    company?.numero || company?.addressNumber,
    company?.bairro,
  ]
    .filter(Boolean)
    .join(", ");
  const cityState = [
    company?.municipio || company?.city,
    company?.uf || company?.state,
  ]
    .filter(Boolean)
    .join(" / ");
  const locationLabel = [addressParts, cityState].filter(Boolean).join(" • ");
  const detailRows = [
    {
      label: "Segmento",
      value:
        company?.segmentName ||
        company?.segment ||
        company?.segment?.name ||
        company?.businessArea ||
        company?.industry,
    },
    {
      label: "CNAE",
      value:
        company?.cnae ||
        company?.cnaePrincipal ||
        company?.cnae_principal ||
        company?.mainCnae,
    },
    {
      label: "Website",
      value: company?.website || company?.site || company?.websiteUrl,
    },
    { label: "Endereço", value: locationLabel },
    {
      label: "CEP",
      value: company?.zipCode || company?.postalCode || company?.cep,
    },
    {
      label: "Responsável",
      value:
        company?.responsibleName ||
        company?.responsible ||
        company?.representative ||
        company?.ownerName ||
        company?.contactName,
    },
    {
      label: "Criada em",
      value:
        formatDate(
          company?.createdAt || company?.created_at || company?.creationDate
        ) || "—",
    },
    {
      label: "Atualizada em",
      value: formatDate(
        company?.updatedAt || company?.updated_at || company?.lastUpdate
      ),
    },
  ].filter((row) => row.value && row.value !== "—");

  const [teamMembersModal, setTeamMembersModal] = useState({
    isOpen: false,
    loading: false,
    members: [],
    team: null,
    error: "",
  });
  const [teamEditModal, setTeamEditModal] = useState({
    open: false,
    team: null,
    form: { name: "", description: "" },
    loading: false,
    error: "",
  });
  const [teamDeleteModal, setTeamDeleteModal] = useState({
    open: false,
    team: null,
    loading: false,
  });
  const [userEditModal, setUserEditModal] = useState({
    open: false,
    user: null,
    form: { name: "", email: "", permission: "" },
    loading: false,
    error: "",
  });
  const [userDeleteModal, setUserDeleteModal] = useState({
    open: false,
    user: null,
    loading: false,
  });
  const [funnelModal, setFunnelModal] = useState({
    open: false,
    funnel: null,
    detail: null,
    loading: false,
    error: "",
  });

  const refreshCompany = () => {
    if (companyId) queryClient.invalidateQueries(["company-detail", companyId]);
  };

  const handleShowTeamMembers = async (team) => {
    if (!team?.id) return;
    setTeamMembersModal({
      isOpen: true,
      loading: true,
      members: [],
      team,
      error: "",
    });
    try {
      const response = await axios.get(TEAMS.LIST_USERS(team.id));
      const list = response?.data?.data || response?.data || [];
      setTeamMembersModal((prev) => ({
        ...prev,
        loading: false,
        members: Array.isArray(list) ? list : [],
      }));
    } catch (err) {
      setTeamMembersModal((prev) => ({
        ...prev,
        loading: false,
        error: "Não foi possível carregar a lista de membros.",
      }));
    }
  };

  const closeTeamMembers = () =>
    setTeamMembersModal((prev) => ({ ...prev, isOpen: false }));

  const openTeamEditModal = (team) => {
    setTeamEditModal({
      open: true,
      team,
      form: {
        name: team?.name || "",
        description: team?.description || "",
      },
      loading: false,
      error: "",
    });
  };

  const closeTeamEditModal = () =>
    setTeamEditModal((prev) => ({ ...prev, open: false }));

  const saveTeamChanges = async () => {
    const { team, form } = teamEditModal;
    if (!team?.id) return;
    setTeamEditModal((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      await axios.put(TEAMS.UPDATE(team.id), {
        name: form.name,
        description: form.description,
      });
      refreshCompany();
      closeTeamEditModal();
    } catch (err) {
      setTeamEditModal((prev) => ({
        ...prev,
        loading: false,
        error: "Não foi possível salvar as alterações.",
      }));
    }
  };

  const openTeamDeleteModal = (team) => {
    setTeamDeleteModal({ open: true, team, loading: false });
  };

  const closeTeamDeleteModal = () =>
    setTeamDeleteModal((prev) => ({ ...prev, open: false }));

  const handleDeleteTeam = async () => {
    const { team } = teamDeleteModal;
    if (!team?.id) return;
    setTeamDeleteModal((prev) => ({ ...prev, loading: true }));
    try {
      await axios.delete(TEAMS.DELETE(team.id));
      refreshCompany();
      closeTeamDeleteModal();
    } catch (err) {
      console.error(err);
      setTeamDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const openUserEditModal = (user) => {
    setUserEditModal({
      open: true,
      user,
      form: {
        name: user?.name || "",
        email: user?.email || "",
        permission: user?.permissionId || "",
      },
      loading: false,
      error: "",
    });
  };

  const closeUserEditModal = () =>
    setUserEditModal((prev) => ({ ...prev, open: false }));

  const saveUserChanges = async () => {
    const { user, form } = userEditModal;
    if (!user?.id) return;
    setUserEditModal((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      await axios.put(`${USERS.GET_USER}/${user.id}`, {
        name: form.name,
        email: form.email,
        permissionId: form.permission,
      });
      refreshCompany();
      closeUserEditModal();
    } catch (err) {
      setUserEditModal((prev) => ({
        ...prev,
        loading: false,
        error: "Não foi possível atualizar as informações do usuário.",
      }));
    }
  };

  const openUserDeleteModal = (user) => {
    setUserDeleteModal({ open: true, user, loading: false });
  };

  const closeUserDeleteModal = () =>
    setUserDeleteModal((prev) => ({ ...prev, open: false }));

  const handleDeleteUser = async () => {
    const { user } = userDeleteModal;
    if (!user?.id) return;
    setUserDeleteModal((prev) => ({ ...prev, loading: true }));
    try {
      await axios.delete(`${USERS.GET_USER}/${user.id}`);
      refreshCompany();
      closeUserDeleteModal();
    } catch (err) {
      console.error(err);
      setUserDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleShowFunnelDetails = async (funnel) => {
    const funnelId = funnel?.id || funnel?.funnelId;
    if (!funnelId) return;
    setFunnelModal({
      open: true,
      funnel,
      detail: null,
      automations: [],
      loading: true,
      error: "",
    });
    try {
      const [detailResponse, automationResponse] = await Promise.all([
        axios.get(`${CRM.FUNNEL_DETAILS}/${funnelId}/details`),
        axios.get(CRM.AUTOMATIONS_LIST(funnelId)),
      ]);
      const detailData =
        detailResponse?.data?.data || detailResponse?.data || null;
      const normalizedStages = (detailData?.stages || []).map((stage) => {
        const cards = Array.isArray(stage.cards) ? stage.cards : [];
        const sorted = [...cards].sort((a, b) => {
          const pa = Number.isFinite(+a.priority) ? +a.priority : Infinity;
          const pb = Number.isFinite(+b.priority) ? +b.priority : Infinity;
          return pa - pb;
        });
        return {
          ...stage,
          cards: sorted.map((card, index) => ({
            ...card,
            priority: Number.isFinite(+card.priority)
              ? +card.priority
              : index + 1,
          })),
        };
      });
      const normalizedDetail = detailData
        ? { ...detailData, stages: normalizedStages }
        : null;
      const automationsRaw = automationResponse?.data?.data;
      const automationsFallback = Array.isArray(automationsRaw)
        ? automationsRaw
        : Array.isArray(automationResponse?.data)
        ? automationResponse.data
        : [];
      setFunnelModal((prev) => ({
        ...prev,
        detail: normalizedDetail,
        automations: automationsFallback,
        loading: false,
      }));
    } catch (err) {
      console.error("Erro ao carregar detalhes do funil:", err);
      setFunnelModal((prev) => ({
        ...prev,
        automations: [],
        loading: false,
        error: "Não foi possível carregar os detalhes do funil.",
      }));
    }
  };

  const closeFunnelModal = () =>
    setFunnelModal((prev) => ({ ...prev, open: false }));

  const funnelDetail = funnelModal.detail;
  const stageList = Array.isArray(funnelDetail?.stages)
    ? funnelDetail.stages
    : [];
  const stageLookup = new Map(
    stageList.map((stage) => [String(stage.id), stage])
  );
  const stageSummaries = stageList.map((stage, index) => {
    const cardsCount =
      Array.isArray(stage.cards) && stage.cards.length
        ? stage.cards.length
        : Number.isFinite(+stage.cardsCount)
        ? +stage.cardsCount
        : Number.isFinite(+stage.cardsTotal)
        ? +stage.cardsTotal
        : 0;
    const automationCount = Array.isArray(stage.automations)
      ? stage.automations.length
      : Number.isFinite(+stage.automationCount)
      ? +stage.automationCount
      : 0;
    return {
      id: stage.id || `stage-${index}`,
      name: stage.name || stage.title || `Etapa ${index + 1}`,
      status: stage.status || stage.visibility || "—",
      cardsCount,
      automationCount,
      order:
        Number.isFinite(+stage.order) || Number.isFinite(+stage.position)
          ? +stage.order || +stage.position
          : index + 1,
      responsible:
        stage.team?.name ||
        stage.responsible?.name ||
        stage.owner?.name ||
        stage.personnel?.name ||
        null,
    };
  });
  const totalCardsInFunnel = stageSummaries.reduce(
    (sum, stage) => sum + stage.cardsCount,
    0
  );
  const totalAutomations = (funnelModal.automations || []).length;
  const funnelVisibility =
    funnelDetail?.visibility ||
    funnelDetail?.visibilityName ||
    funnelDetail?.publicity ||
    "Pública";
  const funnelOwner =
    funnelDetail?.teamName ||
    funnelDetail?.team?.name ||
    funnelDetail?.ownerName ||
    funnelDetail?.owner?.name ||
    funnelDetail?.leader?.name ||
    null;

  const describeTriggerSummary = (automation = {}) => {
    const triggers = Array.isArray(automation.triggers)
      ? automation.triggers
      : automation.trigger
      ? [automation.trigger]
      : [];
    if (!triggers.length) {
      return (
        automation.triggerEvent ||
        automation.event ||
        automation.triggerName ||
        ""
      );
    }
    return triggers
      .map((trigger) => {
        const stageId = trigger.stageId || trigger.stage?.id;
        const stage =
          stageLookup.get(String(stageId)) ||
          stageLookup.get(String(trigger.stage?.id));
        const stageName =
          stage?.name || trigger.stage?.name || trigger.stageName || null;
        const eventLabel = trigger.event || trigger.type || "Evento";
        return [eventLabel, stageName].filter(Boolean).join(" • ");
      })
      .filter(Boolean)
      .join(" | ");
  };

  const describeActionsSummary = (automation = {}) => {
    const actions = Array.isArray(automation.actions)
      ? automation.actions
      : automation.action
      ? [automation.action]
      : [];
    if (!actions.length) return "";
    return actions
      .map(
        (action) =>
          action.name ||
          action.type ||
          action.label ||
          action.title ||
          "Ação personalizada"
      )
      .filter(Boolean)
      .join(" • ");
  };
  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="flex flex-col items-center gap-2 text-sm text-slate-600">
          <Spinner size="xl" />
          Carregando detalhe da empresa...
        </div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="space-y-4">
        <Alert color="failure">
          <span className="font-semibold">
            Não foi possível carregar os detalhes.
          </span>{" "}
          Tente novamente mais tarde.
        </Alert>
        <Link to="/companies">
          <Button color="gray">Voltar para empresas</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="space-y-4">
        <CorporateBreadcrumb
          items={[
            { label: "Empresas", href: "/companies" },
            { label: companyName, current: true },
          ]}
        />
        <div
          className="rounded-3xl bg-gradient-to-br from-white via-slate-50 to-white p-6 text-slate-900 shadow-md border border-slate-200"
          style={{ background: "var(--primary)" }}
        >
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-2xl space-y-3">
              <p className="text-xs uppercase tracking-[0.4em] text-white">
                Empresa
              </p>
              <h1 className="text-3xl font-bold tracking-tight text-white">
                {company.socialReason || company.name}
              </h1>
              <p className="text-sm text-white">
                {company.document || "Documento não informado"} ·{" "}
                {company.email}
              </p>
              <p className="text-sm text-white">
                {company.phoneNumber
                  ? `Telefone: ${company.phoneNumber}`
                  : "Telefone não informado"}
              </p>
            </div>
            <div className="flex flex-wrap items-start gap-3">
              <Badge
                color={badgeColor}
                className="text-sm uppercase tracking-[0.2em]"
              >
                {isTrial ? "Teste" : planStatus}
              </Badge>
            </div>
          </div>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {summaryStats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-2xl bg-white p-4 shadow-sm"
              >
                <p className="text-[0.65rem] uppercase tracking-[0.4em] text-slate-500">
                  {stat.label}
                </p>
                <p className="text-3xl font-semibold text-slate-900">
                  {stat.value}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl bg-white p-4">
              <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                Plano
              </p>
              <p className="text-lg font-semibold text-slate-900">{planName}</p>
              <p className="text-sm text-slate-600">{planCycleLabel}</p>
              <p className="text-sm text-slate-600">
                Expira em: {formatDate(planExpiration)}
              </p>
            </div>
            <div className="rounded-2xl bg-white p-4">
              <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                Licenças
              </p>
              <p className="text-lg font-semibold text-slate-900">
                {subscription?.licenseLimit ?? "—"}
              </p>
              <p className="text-sm text-slate-600">
                Ativas: {subscription?.activeLicenses ?? "—"}
              </p>
              {subscription?.planValue && (
                <p className="text-sm text-slate-300">
                  Valor próximo pagamento:{" "}
                  {formatCurrency(subscription.planValue)}
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      <section className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4 rounded-3xl border border-slate-200 bg-white p-5 shadow-md">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-600">
              Resumo do plano
            </p>
            <Badge className="w-fit" color={badgeColor}>
              {isTrial ? "Teste" : planStatus}
            </Badge>
          </div>
          <div className="space-y-1">
            <p className="text-lg font-semibold text-slate-900">{planName}</p>
            <p className="text-sm text-slate-600">{planCycleLabel}</p>
            <p className="text-sm text-slate-600">
              Expira em: {formatDate(planExpiration)}
            </p>
            <p className="text-sm text-slate-600">
              Licenças: {subscription?.licenseLimit ?? "—"} (ativas:{" "}
              {subscription?.activeLicenses ?? "—"})
            </p>
            {subscription?.planValue && (
              <p className="text-sm text-slate-600">
                Valor próximo pagamento:{" "}
                {formatCurrency(subscription.planValue)}
              </p>
            )}
          </div>
        </div>
        <div className="space-y-3 rounded-3xl border border-slate-200 bg-white p-5 shadow-md">
          <p className="text-sm font-semibold text-slate-600">
            Detalhes da empresa
          </p>
          <p className="text-sm text-slate-600">
            {company.phoneNumber
              ? `Telefone: ${company.phoneNumber}`
              : "Telefone não informado"}
          </p>
          <p className="text-sm text-slate-600">
            Status:
            <span className="ml-2 font-semibold">
              {company.status === 0 && "Bloqueada"}
              {company.status === 1 && "Ativa"}
              {!company.status && "—"}
            </span>
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-md">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-slate-600">Funis</p>
            <Badge className="w-fit">{funnels.length} total</Badge>
          </div>
          {funnels.length === 0 ? (
            <p className="text-sm text-slate-600">Nenhum funil cadastrado.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table hoverable className="min-w-[640px]">
                <Table.Head>
                  <Table.HeadCell>Nome</Table.HeadCell>
                  <Table.HeadCell>Time responsável</Table.HeadCell>
                  <Table.HeadCell>Visibilidade</Table.HeadCell>
                  <Table.HeadCell className="text-right">Ações</Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {funnels.map((funnel) => (
                    <Table.Row key={funnel.id}>
                      <Table.Cell>{funnel.name}</Table.Cell>
                      <Table.Cell>{funnel.team?.name || "—"}</Table.Cell>
                      <Table.Cell className="text-sm text-slate-500">
                        {funnel.visibility || "—"}
                      </Table.Cell>
                      <Table.Cell className="flex justify-end">
                        <Button
                          size="xs"
                          color="info"
                          onClick={() => handleShowFunnelDetails(funnel)}
                        >
                          Abrir
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-md">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-slate-600">Times</p>
            <Badge>{teams.length} ativos</Badge>
          </div>
          {teams.length === 0 ? (
            <p className="text-sm text-slate-600">Nenhum time vinculado.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table hoverable className="min-w-[480px]">
                <Table.Head>
                  <Table.HeadCell>Nome</Table.HeadCell>
                  <Table.HeadCell>Status</Table.HeadCell>
                  <Table.HeadCell className="text-right">Ações</Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {teams.map((team) => (
                    <Table.Row key={team.id}>
                      <Table.Cell>{team.name}</Table.Cell>
                      <Table.Cell>
                        {team.status === 1 ? (
                          <Badge className="w-fit" color="success">
                            Ativo
                          </Badge>
                        ) : (
                          <Badge className="w-fit" color="gray">
                            Inativo
                          </Badge>
                        )}
                      </Table.Cell>
                      <Table.Cell className="flex justify-end gap-2">
                        <Button
                          size="xs"
                          color="light"
                          onClick={() => handleShowTeamMembers(team)}
                        >
                          Membros
                        </Button>
                        <Button
                          size="xs"
                          color="info"
                          onClick={() => openTeamEditModal(team)}
                        >
                          Editar
                        </Button>
                        <Button
                          size="xs"
                          color="failure"
                          onClick={() => openTeamDeleteModal(team)}
                        >
                          Excluir
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-md">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-slate-600">Usuários</p>
            <Badge>{users.length} cadastrados</Badge>
          </div>
          {users.length === 0 ? (
            <p className="text-sm text-slate-600">Nenhum usuário cadastrado.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table hoverable className="min-w-[640px]">
                <Table.Head>
                  <Table.HeadCell>Nome</Table.HeadCell>
                  <Table.HeadCell>E-mail</Table.HeadCell>
                  <Table.HeadCell>Permissão</Table.HeadCell>
                  <Table.HeadCell>Status</Table.HeadCell>
                  <Table.HeadCell className="text-right">Ações</Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {users.map((user) => (
                    <Table.Row key={user.id}>
                      <Table.Cell>{user.name}</Table.Cell>
                      <Table.Cell>{user.email}</Table.Cell>
                      <Table.Cell className="text-sm text-slate-500">
                        {PERMISSION_LABELS[user.permissionId] ||
                          user.permissionId ||
                          "—"}
                      </Table.Cell>
                      <Table.Cell>
                        <Badge
                          className="w-fit"
                          color={user.isActive ? "success" : "gray"}
                        >
                          {user.isActive ? "Ativo" : "Inativo"}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell className="flex justify-end gap-2">
                        <Button
                          size="xs"
                          color="info"
                          onClick={() => openUserEditModal(user)}
                        >
                          Editar
                        </Button>
                        <Button
                          size="xs"
                          color="failure"
                          onClick={() => openUserDeleteModal(user)}
                        >
                          Excluir
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-md">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-slate-600">Pagamentos</p>
            <Badge>{charges.length} lançamentos</Badge>
          </div>
          {charges.length === 0 ? (
            <p className="text-sm text-slate-600">
              Nenhuma cobrança encontrada.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table hoverable className="min-w-[640px]">
                <Table.Head>
                  <Table.HeadCell>Descrição</Table.HeadCell>
                  <Table.HeadCell>Vencimento</Table.HeadCell>
                  <Table.HeadCell>Valor</Table.HeadCell>
                  <Table.HeadCell>Status</Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {charges.map((charge) => (
                    <Table.Row
                      key={
                        charge.id ||
                        `${charge.externalReference}-${charge.dueDate}`
                      }
                    >
                      <Table.Cell>
                        {charge.description ||
                          charge.externalReference ||
                          "Cobrança"}
                      </Table.Cell>
                      <Table.Cell>{formatDate(charge.dueDate)}</Table.Cell>
                      <Table.Cell>{formatCurrency(charge.value)}</Table.Cell>
                      <Table.Cell>
                        <Badge color={badgeColorFromStatus(charge.status)}>
                          {charge.status || "—"}
                        </Badge>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </div>
      </section>
      <Modal
        show={teamMembersModal.isOpen}
        size="md"
        onClose={closeTeamMembers}
      >
        <Modal.Header>
          Membros de {teamMembersModal.team?.name || "time"}
        </Modal.Header>
        <Modal.Body>
          {teamMembersModal.loading ? (
            <div className="flex items-center gap-2">
              <Spinner size="sm" />
              Carregando membros...
            </div>
          ) : teamMembersModal.error ? (
            <p className="text-sm text-red-600">{teamMembersModal.error}</p>
          ) : !teamMembersModal.members.length ? (
            <p className="text-sm text-slate-600">
              Nenhum membro listado para este time.
            </p>
          ) : (
            <ul className="space-y-3">
              {teamMembersModal.members.map((member) => (
                <li
                  key={member.id || member.userId || member.email}
                  className="text-sm text-slate-700"
                >
                  <p className="font-semibold">
                    {member.name ||
                      member.fullName ||
                      member.user?.name ||
                      "Usuário"}
                  </p>
                  <p className="text-xs text-slate-500">
                    {member.email || member.user?.email || "—"}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button color="light" onClick={closeTeamMembers}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={teamEditModal.open} size="md" onClose={closeTeamEditModal}>
        <Modal.Header>Editar time</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            {teamEditModal.error && (
              <p className="text-sm text-red-600">{teamEditModal.error}</p>
            )}
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.4em] text-slate-500">
                Nome
              </p>
              <TextInput
                placeholder="Nome do time"
                value={teamEditModal.form.name}
                onChange={(e) =>
                  setTeamEditModal((prev) => ({
                    ...prev,
                    form: { ...prev.form, name: e.target.value },
                  }))
                }
              />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.4em] text-slate-500">
                Descrição
              </p>
              <Textarea
                placeholder="Descrição curta do escopo"
                value={teamEditModal.form.description}
                onChange={(e) =>
                  setTeamEditModal((prev) => ({
                    ...prev,
                    form: { ...prev.form, description: e.target.value },
                  }))
                }
                rows={3}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="success"
            onClick={saveTeamChanges}
            disabled={teamEditModal.loading}
          >
            {teamEditModal.loading ? <Spinner size="sm" /> : "Salvar"}
          </Button>
          <Button color="light" onClick={closeTeamEditModal}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={teamDeleteModal.open}
        size="md"
        onClose={closeTeamDeleteModal}
      >
        <Modal.Header>Excluir time</Modal.Header>
        <Modal.Body>
          <p className="text-sm text-slate-600">
            Tem certeza que deseja excluir o time{" "}
            <strong>{teamDeleteModal.team?.name}</strong>? Esta ação não pode
            ser revertida, mas você pode recriar o time posteriormente.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="failure"
            onClick={handleDeleteTeam}
            disabled={teamDeleteModal.loading}
          >
            {teamDeleteModal.loading ? <Spinner size="sm" /> : "Excluir"}
          </Button>
          <Button color="light" onClick={closeTeamDeleteModal}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={userEditModal.open} size="md" onClose={closeUserEditModal}>
        <Modal.Header>Editar usuário</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            {userEditModal.error && (
              <p className="text-sm text-red-600">{userEditModal.error}</p>
            )}
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.4em] text-slate-500">
                Nome
              </p>
              <TextInput
                placeholder="Nome completo"
                value={userEditModal.form.name}
                onChange={(e) =>
                  setUserEditModal((prev) => ({
                    ...prev,
                    form: { ...prev.form, name: e.target.value },
                  }))
                }
              />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.4em] text-slate-500">
                E-mail
              </p>
              <TextInput
                placeholder="usuario@empresa.com"
                value={userEditModal.form.email}
                onChange={(e) =>
                  setUserEditModal((prev) => ({
                    ...prev,
                    form: { ...prev.form, email: e.target.value },
                  }))
                }
              />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.4em] text-slate-500">
                Permissão
              </p>
              <Select
                value={userEditModal.form.permission}
                onChange={(e) =>
                  setUserEditModal((prev) => ({
                    ...prev,
                    form: { ...prev.form, permission: e.target.value },
                  }))
                }
              >
                <option value="">Selecione a permissão</option>
                {PERMISSION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="success"
            onClick={saveUserChanges}
            disabled={userEditModal.loading}
          >
            {userEditModal.loading ? <Spinner size="sm" /> : "Salvar"}
          </Button>
          <Button color="light" onClick={closeUserEditModal}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={userDeleteModal.open}
        size="md"
        onClose={closeUserDeleteModal}
      >
        <Modal.Header>Excluir usuário</Modal.Header>
        <Modal.Body>
          <p className="text-sm text-slate-600">
            Deseja remover o usuário{" "}
            <strong>{userDeleteModal.user?.name}</strong>? Voce pode recriar o
            cadastro no futuro se precisar.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="failure"
            onClick={handleDeleteUser}
            disabled={userDeleteModal.loading}
          >
            {userDeleteModal.loading ? <Spinner size="sm" /> : "Excluir"}
          </Button>
          <Button color="light" onClick={closeUserDeleteModal}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={funnelModal.open} size="4xl" onClose={closeFunnelModal}>
        <Modal.Header>Detalhes do funil</Modal.Header>
        <Modal.Body>
          {funnelModal.loading ? (
            <div className="flex items-center gap-2">
              <Spinner size="sm" />
              Carregando detalhes...
            </div>
          ) : funnelModal.error ? (
            <p className="text-sm text-red-600">{funnelModal.error}</p>
          ) : (
            <>
              <div className="space-y-3">
                <p className="text-lg font-semibold text-slate-900">
                  {funnelModal.detail?.name || funnelModal.funnel?.name}
                </p>
                <p className="text-sm text-slate-500">
                  {funnelModal.detail?.description ||
                    funnelModal.funnel?.description ||
                    "Descrição não informada."}
                </p>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-[0.65rem] uppercase tracking-[0.4em] text-slate-400">
                    Status
                  </p>
                  <p className="text-sm text-slate-700">
                    {funnelModal.detail?.status ||
                      funnelModal.funnel?.status ||
                      "—"}
                  </p>
                </div>
                <div>
                  <p className="text-[0.65rem] uppercase tracking-[0.4em] text-slate-400">
                    Time responsável
                  </p>
                  <p className="text-sm text-slate-700">{funnelOwner || "—"}</p>
                </div>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-white/70 p-3">
                  <p className="text-[0.6rem] uppercase tracking-[0.4em] text-slate-400">
                    Visibilidade
                  </p>
                  <p className="text-lg font-semibold text-slate-900">
                    {funnelVisibility}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white/70 p-3">
                  <p className="text-[0.6rem] uppercase tracking-[0.4em] text-slate-400">
                    Etapas
                  </p>
                  <p className="text-lg font-semibold text-slate-900">
                    {stageSummaries.length}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white/70 p-3">
                  <p className="text-[0.6rem] uppercase tracking-[0.4em] text-slate-400">
                    Cards
                  </p>
                  <p className="text-lg font-semibold text-slate-900">
                    {totalCardsInFunnel}
                  </p>
                </div>
              </div>
              <div className="mt-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-600">
                    Estágios
                  </p>
                  <span className="text-xs uppercase tracking-[0.4em] text-slate-400">
                    {stageSummaries.length} etapas
                  </span>
                </div>
                {stageSummaries.length ? (
                  <div className="mt-3 grid gap-3">
                    {stageSummaries.map((stage) => (
                      <div
                        key={stage.id}
                        className="rounded-2xl border border-slate-200 bg-slate-50/90 p-4"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-semibold text-slate-900">
                            {stage.order}. {stage.name}
                          </p>
                          <span className="text-[0.6rem] uppercase tracking-[0.4em] text-slate-500">
                            {stage.status}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
                          <span>
                            Cards: <strong>{stage.cardsCount}</strong>
                          </span>
                          <span>
                            Automações: <strong>{stage.automationCount}</strong>
                          </span>
                        </div>
                        {stage.responsible && (
                          <p className="mt-1 text-xs text-slate-500">
                            Responsável: {stage.responsible}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-slate-600">
                    Nenhuma etapa definida para este funil.
                  </p>
                )}
              </div>
              <div className="mt-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-600">
                    Automações
                  </p>
                  <span className="text-xs uppercase tracking-[0.4em] text-slate-400">
                    {totalAutomations} programada(s)
                  </span>
                </div>
                {totalAutomations ? (
                  <div className="mt-3 space-y-3">
                    {funnelModal.automations.map((automation) => {
                      const triggerSummary = describeTriggerSummary(automation);
                      const actionsSummary = describeActionsSummary(automation);
                      return (
                        <div
                          key={automation.id || automation.name}
                          className="rounded-2xl border border-slate-200 bg-white/80 p-4"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-sm font-semibold text-slate-900">
                              {automation.name || `Automação ${automation.id}`}
                            </p>
                            <Badge
                              color={automation.isActive ? "success" : "gray"}
                            >
                              {automation.isActive ? "Ativa" : "Inativa"}
                            </Badge>
                          </div>
                          {!!triggerSummary && (
                            <p className="mt-2 text-xs text-slate-500">
                              Gatilho: {triggerSummary}
                            </p>
                          )}
                          {!!actionsSummary && (
                            <p className="mt-1 text-xs text-slate-500">
                              Ações: {actionsSummary}
                            </p>
                          )}
                          {automation.targetStage?.name && (
                            <p className="mt-1 text-xs text-slate-500">
                              Destino: {automation.targetStage.name}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-slate-600">
                    Nenhuma automação cadastrada para este funil.
                  </p>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button color="light" onClick={closeFunnelModal}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CompanyDetailPage;
